import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import axios from "axios";
import { FadeIn } from "@/components/FadeIn";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WatchReadPage() {
  const [videos, setVideos] = useState([]);
  const [journals, setJournals] = useState([]);
  const [substackPosts, setSubstackPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/videos`).then((r) => setVideos(r.data)).catch(() => {});
    axios.get(`${API}/journals`).then((r) => setJournals(r.data)).catch(() => {});
    axios.get(`${API}/substack-feed`).then((r) => setSubstackPosts(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="watch-read-page">
      {/* Hero */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <FadeIn>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl tracking-tight font-light text-foreground">
              Watch & Read
            </h1>
            <p className="font-body text-base md:text-lg text-muted-foreground mt-6 max-w-xl mx-auto leading-relaxed">
              Dialogues and writings exploring another way of living. Nothing to follow. Nothing to believe. Only an invitation to look.
            </p>
          </div>
        </FadeIn>
      </section>

      {/* Watch Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24" style={{ backgroundColor: "#F5F5F0" }}>
        <div className="max-w-screen-xl mx-auto">
          <FadeIn>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight mb-4 text-foreground">Watch</h2>
            <p className="font-body text-sm text-muted-foreground mb-12 max-w-xl">
              Video dialogues exploring the structure of consciousness and daily living.
            </p>
          </FadeIn>

          {videos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video, i) => (
                <FadeIn key={video.id} delay={i * 0.08}>
                  <div data-testid={`video-card-${i}`}>
                    <div className="video-embed">
                      <iframe
                        src={`https://www.youtube.com/embed/${video.youtube_video_id}`}
                        title={video.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>
                    <h4 className="font-heading text-lg mt-4 text-foreground">{video.title}</h4>
                    {video.description && (
                      <p className="font-body text-sm text-muted-foreground mt-1">{video.description}</p>
                    )}
                  </div>
                </FadeIn>
              ))}
            </div>
          ) : (
            <FadeIn>
              <p className="font-body text-sm text-muted-foreground italic">Videos will be added soon.</p>
            </FadeIn>
          )}

          <FadeIn delay={0.3}>
            <div className="mt-12">
              <a
                href="https://www.youtube.com/@bikram_jha"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-body text-sm tracking-[0.15em] uppercase text-[#5C6B5D] hover:underline underline-offset-4 decoration-1 transition-all duration-300"
                data-testid="view-youtube-archive"
              >
                View Full Archive on YouTube <ExternalLink size={14} strokeWidth={1.5} />
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      <Separator style={{ backgroundColor: "#E5E4DE" }} />

      {/* Read Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-screen-xl mx-auto">
          <FadeIn>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight mb-4 text-foreground">Read</h2>
            <p className="font-body text-sm text-muted-foreground mb-12 max-w-xl">
              Writing that grows out of observation, not theory. A journal exploring self-inquiry, education, ecology, and inner responsibility.
            </p>
          </FadeIn>

          {/* Substack Posts */}
          {substackPosts.length > 0 && (
            <>
              <FadeIn>
                <h3 className="font-heading text-xl md:text-2xl tracking-tight mb-6 text-foreground">From Substack</h3>
              </FadeIn>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {substackPosts.map((post, i) => (
                  <FadeIn key={i} delay={i * 0.08}>
                    <a
                      href={post.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group block p-6 border border-[hsl(48,12%,88%)] hover:border-[#5C6B5D]/40 transition-colors duration-300"
                      data-testid={`substack-post-${i}`}
                    >
                      <h4 className="font-heading text-lg font-medium text-foreground group-hover:text-[#5C6B5D] transition-colors duration-300 mb-2 line-clamp-2">
                        {post.title}
                      </h4>
                      <p className="font-body text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-3">
                        {post.description}...
                      </p>
                      <span className="inline-flex items-center gap-1 font-body text-xs text-[#5C6B5D]">
                        Read on Substack <ExternalLink size={12} strokeWidth={1.5} />
                      </span>
                    </a>
                  </FadeIn>
                ))}
              </div>
              <FadeIn>
                <div className="mb-12">
                  <a
                    href="https://bikramjha.substack.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-body text-sm tracking-[0.15em] uppercase text-[#5C6B5D] hover:underline underline-offset-4 decoration-1 transition-all duration-300"
                    data-testid="view-all-substack"
                  >
                    View All on Substack <ExternalLink size={14} strokeWidth={1.5} />
                  </a>
                </div>
              </FadeIn>
              <Separator className="mb-12" style={{ backgroundColor: "#E5E4DE" }} />
            </>
          )}

          {/* Local Journal Posts */}
          {journals.length > 0 && (
            <>
              <FadeIn>
                <h3 className="font-heading text-xl md:text-2xl tracking-tight mb-6 text-foreground">Journal</h3>
              </FadeIn>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                {journals.map((journal, i) => (
                  <FadeIn key={journal.id} delay={i * 0.1}>
                    <Link
                      to={`/journal/${journal.slug}`}
                      className="group block"
                      data-testid={`journal-card-${i}`}
                    >
                      {journal.featured_image && (
                        <div className="overflow-hidden mb-5">
                          <img
                            src={journal.featured_image}
                            alt={journal.title}
                            className="w-full aspect-[16/9] object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-700"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="flex gap-2 mb-3">
                        {journal.tags?.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="outline" className="font-body text-[10px] tracking-wider uppercase border-[hsl(48,12%,88%)] text-muted-foreground">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <h3 className="font-heading text-xl md:text-2xl font-medium text-foreground group-hover:text-[#5C6B5D] transition-colors duration-300 mb-2">
                        {journal.title}
                      </h3>
                      <p className="font-body text-sm text-muted-foreground leading-relaxed line-clamp-3">
                        {journal.excerpt}
                      </p>
                      <p className="font-body text-xs text-muted-foreground/60 mt-3">
                        {new Date(journal.publish_date).toLocaleDateString("en-US", {
                          month: "long", day: "numeric", year: "numeric",
                        })}
                      </p>
                    </Link>
                  </FadeIn>
                ))}
              </div>
            </>
          )}

          {journals.length === 0 && substackPosts.length === 0 && (
            <FadeIn>
              <p className="font-body text-sm text-muted-foreground italic">Journal entries coming soon.</p>
            </FadeIn>
          )}
        </div>
      </section>
    </div>
  );
}
